import { useCallback, useEffect, useRef, useState } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

interface UseVoiceInputProps {
  onTranscript: (text: string) => void;
  language?: string;
}

interface UseVoiceInputReturn {
  isListening: boolean;
  isSupported: boolean;
  isMicrophoneAvailable: boolean;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
  error: string | null;
  interimTranscript: string;
}

const useVoiceInput = ({
  onTranscript,
  language = 'en-US',
}: UseVoiceInputProps): UseVoiceInputReturn => {
  const [error, setError] = useState<string | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
  } = useSpeechRecognition();

  const resetTranscriptRef = useRef(resetTranscript);
  resetTranscriptRef.current = resetTranscript;

  // Send transcript when speech ends
  useEffect(() => {
    if (!listening && transcript) {
      onTranscriptRef.current(transcript);
      resetTranscriptRef.current();
    }
  }, [listening, transcript]);

  const startListening = useCallback(async () => {
    setError(null);
    try {
      await SpeechRecognition.startListening({
        continuous: false,
        language,
      });
    } catch {
      setError('Failed to start voice recognition');
    }
  }, [language]);

  const stopListening = useCallback(async () => {
    await SpeechRecognition.stopListening();
  }, []);

  return {
    isListening: listening,
    isSupported: browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
    startListening,
    stopListening,
    error,
    interimTranscript: transcript,
  };
};

export default useVoiceInput;
